import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, X, User } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function NewDirectThreadDialog({ open, onOpenChange, onCreated, initialStudent }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(initialStudent || null);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const { data: students = [] } = useQuery({
    queryKey: ["students-picker"],
    queryFn: async () => (await api.get("/students", { params: { limit: 500 } })).data,
    enabled: open,
  });

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return students.slice(0, 25);
    return students.filter((s) =>
      `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase().includes(t)
      || (s.matricola || "").toLowerCase().includes(t)
      || (s.email || "").toLowerCase().includes(t)
    ).slice(0, 25);
  }, [students, q]);

  const mut = useMutation({
    mutationFn: async () => (await api.post("/threads", {
      subject: subject.trim(),
      student_id: selected.id,
      initial_message: message.trim() || null,
    })).data,
    onSuccess: (d) => {
      toast.success(`Conversazione con ${selected.first_name} ${selected.last_name} creata`);
      qc.invalidateQueries({ queryKey: ["threads"] });
      reset();
      onOpenChange(false);
      onCreated?.(d);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore creazione"),
  });

  function reset() {
    setSelected(null); setQ(""); setSubject(""); setMessage("");
  }

  function submit(e) {
    e.preventDefault();
    if (!selected || !subject.trim()) return;
    mut.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-xl bg-white">
        <DialogHeader>
          <DialogTitle>Nuovo messaggio diretto</DialogTitle>
          <DialogDescription>Apri una conversazione 1-a-1 con un singolo studente.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Studente</Label>
            {selected ? (
              <div
                data-testid="nd-selected"
                className="flex items-center gap-3 p-2.5 border border-[#1C352D] rounded-lg bg-[#FDFBF7]"
              >
                <div className="w-8 h-8 rounded-full bg-[#1C352D] text-white text-xs font-semibold grid place-items-center">
                  {(selected.first_name?.[0] || "") + (selected.last_name?.[0] || "")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{selected.last_name} {selected.first_name}</div>
                  <div className="text-[11px] text-stone-500 truncate">
                    {selected.matricola} · {selected.email || "senza email"}
                    {selected.year_level && ` · ${selected.year_level}° anno`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  data-testid="nd-clear"
                  className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
                  title="Cambia"
                ><X size={14}/></button>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                  <Input
                    autoFocus
                    data-testid="nd-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Cerca per nome, matricola o email…"
                    className="pl-9"
                  />
                </div>
                <div className="mt-2 max-h-56 overflow-y-auto border border-stone-200 rounded-lg">
                  {filtered.length === 0 ? (
                    <div className="p-6 text-center text-xs text-stone-400">Nessuno studente trovato.</div>
                  ) : (
                    <ul className="divide-y divide-stone-100">
                      {filtered.map((s) => (
                        <li key={s.id}>
                          <button
                            type="button"
                            data-testid={`nd-student-${s.id}`}
                            onClick={() => setSelected(s)}
                            className="w-full text-left p-2.5 flex items-center gap-3 hover:bg-stone-50/70"
                          >
                            <div className="w-7 h-7 rounded-full bg-stone-100 text-stone-600 text-[10px] font-semibold grid place-items-center">
                              {(s.first_name?.[0] || "") + (s.last_name?.[0] || "")}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{s.last_name} {s.first_name}</div>
                              <div className="text-[10px] text-stone-500 truncate">
                                {s.matricola}{s.email && ` · ${s.email}`}
                              </div>
                            </div>
                            {s.year_level && <Badge className="bg-[#1C352D] text-white text-[9px] rounded-full">{s.year_level}° anno</Badge>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>

          <div>
            <Label className="text-xs">Oggetto</Label>
            <Input
              data-testid="nd-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              placeholder="es. Colloquio individuale, riordinamento piano di studi…"
            />
          </div>
          <div>
            <Label className="text-xs">Primo messaggio (facoltativo)</Label>
            <Textarea
              rows={4}
              data-testid="nd-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Scrivi il primo messaggio…"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button
              type="submit"
              data-testid="nd-submit"
              disabled={mut.isPending || !selected || !subject.trim()}
              className="bg-[#1C352D] hover:bg-[#264A3E]"
            >
              {mut.isPending ? "Creazione…" : "Apri conversazione"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
